import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TerminalCalendarButton = ({ 
  name, 
  location, 
  startDate, 
  endDate, 
  startTime, 
  endTime, 
  timeZone 
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);
  
  // Handle click outside to close the popup
  useEffect(() => {
    if (!showPopup) return;
    
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };
    
    // Add slight delay to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopup]);
  
  // Format dates for different calendar services
  const formatDate = (date, time, format) => {
    if (!date || !time) return '';
    
    const [year, month, day] = date.split('-');
    const [hours, minutes] = time.split(':');
    
    switch (format) {
      case 'google':
        // Format: YYYYMMDDTHHMMSSZ
        return `${year}${month}${day}T${hours}${minutes}00${timeZone ? '' : 'Z'}`;
      case 'outlook':
        // Format: YYYY-MM-DDThh:mm:ss
        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
      case 'ical':
        // Format: YYYYMMDDTHHMMSS
        return `${year}${month}${day}T${hours}${minutes}00`;
      default:
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
  };

  // Generate calendar URLs
  const getGoogleCalendarUrl = () => {
    const start = formatDate(startDate, startTime, 'google');
    const end = formatDate(endDate, endTime, 'google');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(name)}&dates=${start}/${end}&location=${encodeURIComponent(location)}`;
  };

  const getOutlookUrl = () => {
    const start = formatDate(startDate, startTime, 'outlook');
    const end = formatDate(endDate, endTime, 'outlook');
    return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(name)}&startdt=${encodeURIComponent(start)}&enddt=${encodeURIComponent(end)}&location=${encodeURIComponent(location)}`;
  };

  const getMicrosoft365Url = () => {
    const start = formatDate(startDate, startTime, 'outlook');
    const end = formatDate(endDate, endTime, 'outlook');
    return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(name)}&startdt=${encodeURIComponent(start)}&enddt=${encodeURIComponent(end)}&location=${encodeURIComponent(location)}`;
  };

  // Generate iCalendar file content
  const generateICalContent = () => {
    const icalStart = formatDate(startDate, startTime, 'ical');
    const icalEnd = formatDate(endDate, endTime, 'ical');
    
    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Camp Apex//Terminal Calendar//EN
BEGIN:VEVENT
UID:${Date.now()}@campapex.org
DTSTAMP:${new Date().toISOString().replace(/[-:.]/g, '').split('T')[0]}T${new Date().toISOString().replace(/[-:.]/g, '').split('T')[1].split('.')[0]}Z
DTSTART:${icalStart}
DTEND:${icalEnd}
SUMMARY:${name}
LOCATION:${location}
END:VEVENT
END:VCALENDAR`;
  };

  // Download iCal file
  const downloadICalFile = () => {
    const icalContent = generateICalContent();
    const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/\s+/g, '-').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calendar options
  const calendarOptions = [
    { 
      name: 'Google Calendar', 
      action: () => window.open(getGoogleCalendarUrl(), '_blank'),
      color: 'text-green-400'
    },
    { 
      name: 'Apple Calendar', 
      action: downloadICalFile,
      color: 'text-yellow-400'
    },
    { 
      name: 'iCal Format', 
      action: downloadICalFile,
      color: 'text-green-400'
    },
    { 
      name: 'Outlook.com', 
      action: () => window.open(getOutlookUrl(), '_blank'),
      color: 'text-yellow-400'
    },
    { 
      name: 'Microsoft 365', 
      action: () => window.open(getMicrosoft365Url(), '_blank'),
      color: 'text-green-400'
    }
  ];

  // Popup component
  const CalendarPopup = () => {
    return createPortal(
      <div 
        className="fixed inset-0 bg-zinc-900/80 backdrop-blur-sm flex items-center justify-center z-50"
        aria-modal="true"
        role="dialog"
      >
                  <div 
          ref={popupRef}
          className="bg-zinc-800/95 border-2 border-green-500 rounded-lg p-5 shadow-lg max-w-md w-11/12 font-mono"
        >
          <div className="text-green-400 mb-4 text-center text-xl font-bold tracking-wide">
            ADD TO CALENDAR
          </div>
          
          <div className="text-zinc-200 mb-4 text-center text-sm tracking-wide">
            {name}
          </div>
          
          <div className="space-y-2">
            {calendarOptions.map((option, index) => (
              <button
                key={index}
                onClick={option.action}
                className={`bg-zinc-900 w-full p-3 ${option.color} text-center border border-zinc-700 rounded hover:bg-zinc-800 transition-colors font-bold tracking-wide text-sm`}
              >
                {option.name}
              </button>
            ))}
          </div>
          
          <div className="text-center mt-5">
            <button
              onClick={() => setShowPopup(false)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded transition-colors font-bold tracking-wide"
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="pb-4">
      <button
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-mono text-sm h-9 w-60 block mx-auto transition-colors"
        onClick={() => setShowPopup(true)}
      >
        ADD TO CALENDAR
      </button>
      
      {showPopup && <CalendarPopup />}
    </div>
  );
};

export default TerminalCalendarButton;